import { Injectable } from '@angular/core';
import { WalletTierData } from '../_model/compliance';
import { FormGroup } from '@angular/forms';
import { WalletDetailsDto } from '../_model/wallet';

@Injectable({ providedIn: 'root' })
export class FormatService {
  smartNumber(value: number | null | undefined, normalDp = 2, sigDigits = 2): string {
    if (value === null || value === undefined || isNaN(value as any)) return '0';

    const abs = Math.abs(value);

    if (abs === 0) return '0';

    if (abs >= 0.01) {
      return value.toFixed(normalDp);
    }

    return Number(value.toPrecision(sigDigits)).toString();
  }

  smartAmount(value: number | string | null | undefined): string {
    const amount = this.toNumber(value);
    if (!Number.isFinite(amount)) return '0.00';

    const sign = amount < 0 ? '-' : '';
    const abs = Math.abs(amount);

    if (abs === 0) return '0.00';

    if (abs >= 1) {
      return `${sign}${this.formatGrouped(abs, 2, 2)}`;
    }

    return `${sign}${this.formatTinyAmount(value, abs)}`;
  }

  smartCurrencyAmount(value: number | string | null | undefined, symbol?: string | null): string {
    const amount = this.toNumber(value);
    const prefix = symbol || '';

    if (amount < 0) {
      return `-${prefix}${this.smartAmount(Math.abs(amount))}`;
    }

    return `${prefix}${this.smartAmount(amount)}`;
  }

  getLimitData(
    tierInfo: WalletTierData | null,
    activityType: string,
    key: "remainingDailyLimit" | "remainingMonthlyLimit" | "perTransactionLimit" | "dailyLimit" | "monthlyLimit"
  ): number {
    if (!tierInfo) return 0;

    const limit = tierInfo.limits.find(
      x => x.activityType.toLowerCase() === activityType.toLowerCase()
    );

    return Number(limit?.[key] ?? 0);
  }

  checkLimit(amount: number, walletDat: WalletDetailsDto, activityType: string): boolean {
    amount = Number(amount || 0);
    if (amount <= 0) return false;

    const tier = walletDat?.tierInfo;
    if (!tier) return false;

    const limit = tier.limits.find(
      x => x.activityType.toLowerCase() === activityType.toLowerCase()
    );

    if (!limit) return false;
    if (!limit.isAllowed) return false;

    if (!limit.isUnlimited) {
      if (
        limit.perTransactionLimit == null ||
        limit.dailyLimit == null ||
        limit.monthlyLimit == null
      ) {
        return false;
      }

      if (amount > limit.perTransactionLimit) return false;

      if (
        limit.remainingDailyLimit != null &&
        amount > limit.remainingDailyLimit
      ) return false;

      if (
        limit.remainingMonthlyLimit != null &&
        amount > limit.remainingMonthlyLimit
      ) return false;
    }

    return true;
  }

  toNumber(v: any): number {
    if (v === null || v === undefined || v === '') return 0;
    const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
  }

  private formatGrouped(value: number, minFractionDigits: number, maxFractionDigits: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits
    }).format(value);
  }

  private formatTinyAmount(rawValue: number | string | null | undefined, absValue: number): string {
    const plain = this.toPlainDecimal(rawValue, absValue);
    const decimals = plain.split('.')[1] || '';
    let nonZeroDigits = 0;

    for (let i = 0; i < decimals.length; i++) {
      if (decimals[i] !== '0') {
        nonZeroDigits++;
        if (nonZeroDigits === 2) {
          return this.trimTrailingZeros(`0.${decimals.slice(0, i + 1)}`);
        }
      }
    }

    return this.trimTrailingZeros(plain);
  }

  private toPlainDecimal(rawValue: number | string | null | undefined, absValue: number): string {
    const raw = String(rawValue ?? '').replace(/,/g, '').trim();
    const normalized = raw.replace(/^-/, '');

    if (normalized && !/e/i.test(normalized) && normalized.includes('.')) {
      return this.trimTrailingZeros(normalized);
    }

    const text = absValue.toString();
    if (!/e/i.test(text)) return this.trimTrailingZeros(text);

    return this.trimTrailingZeros(absValue.toFixed(20));
  }

  private trimTrailingZeros(value: string): string {
    if (!value.includes('.')) return value;

    const trimmed = value.replace(/0+$/, '').replace(/\.$/, '');
    return trimmed === '0' ? '0.00' : trimmed;
  }

  toUtcIsoDateStart(value: string | null | undefined): string | null {
    if (!value) return null;
    return `${value}T00:00:00Z`;
  }

  formatPrice(n: number, dp: number = 11): string {
    // keep as string so you don't lose precision in UI
    // adjust digits as you like
    return Number.isFinite(n) ? n.toFixed(dp).replace(/\.?0+$/, '') : '';
  }

  getUserStatusClass(status: string, outline: boolean = false): string {
    status = status.toLowerCase().charAt(0).toUpperCase() + status.slice(1);
    switch (status) {
      case 'New': return outline ? 'badge text-bg-primary-subtle border border-primary-subtle text-primary' : 'badge text-bg-primary';
      case 'Suspended': return outline ? 'badge text-bg-warning-subtle border border-warning-subtle text-warning' : 'badge text-bg-warning';
      case 'Active': return outline ? 'badge text-bg-success-subtle border border-success-subtle text-success' : 'badge text-bg-success';
      case 'Inactive': return outline ? 'badge text-bg-secondary-subtle border border-secondary-subtle text-secondary' : 'badge text-bg-secondary';
      case 'Banned': return outline ? 'badge text-bg-danger-subtle border border-danger-subtle text-danger' : 'badge text-bg-danger';
      case 'Deleted': return outline ? 'badge text-bg-dark-subtle border border-dark-subtle text-dark' : 'badge text-bg-dark';
      default: return '';
    }
  }

  getOrderStatusClass(status: string, outline: boolean = false): string {
    status = status.toLowerCase().charAt(0).toUpperCase() + status.slice(1);
    switch (status) {
      case 'Open': return outline ? 'badge text-bg-primary-subtle border border-primary-subtle text-primary' : 'badge text-bg-primary';
      case 'Partial': return outline ? 'badge text-bg-warning-subtle border border-warning-subtle text-warning' : 'badge text-bg-warning';
      case 'Completed': return outline ? 'badge text-bg-success-subtle border border-success-subtle text-success' : 'badge text-bg-success';
      case 'Cancelled': return outline ? 'badge text-bg-info-subtle border border-info-subtle text-info' : 'badge text-bg-info';
      case 'PartiallyCancelled':
        return outline
          ? 'badge text-bg-info-subtle border border-info-subtle text-info'
          : 'badge text-bg-info';

      case 'RateChanged':
        return outline
          ? 'badge text-bg-secondary-subtle border border-secondary-subtle text-secondary'
          : 'badge text-bg-secondary';
      case 'Failed': return outline ? 'badge text-bg-danger-subtle border border-danger-subtle text-danger' : 'badge text-bg-danger';
      default: return '';
    }
  }

  getGeneralStatusClass(status: string, outline: boolean = false): string {
    status = status.toLowerCase().charAt(0).toUpperCase() + status.slice(1);
    switch (status) {
      case 'Active': return outline ? 'badge text-bg-success-subtle border border-success-subtle text-success' : 'badge text-bg-success';
      case 'Inactive': return outline ? 'badge text-bg-warning-subtle border border-warning-subtle text-warning' : 'badge text-bg-warning';
      case 'Cancelled': return outline ? 'badge text-bg-info-subtle border border-info-subtle text-info' : 'badge text-bg-info';
      case 'Deleted': return outline ? 'badge text-bg-danger-subtle border border-danger-subtle text-danger' : 'badge text-bg-danger';
      default: return '';
    }
  }

  getPlusMinus(value: number, outline: boolean = false): string {
    if (value > 0) {
      return "badge text-bg-success-subtle border border-success-subtle text-success"
    } else if (value < 0) {
      return "badge text-bg-danger-subtle border border-danger-subtle text-danger"
    } else {
      return "badge text-bg-warning-subtle border border-warning-subtle text-warning"
    }
  }

  getHoldStatusClass(status: string, outline: boolean = false): string {
    status = status.toLowerCase().charAt(0).toUpperCase() + status.slice(1);
    switch (status) {
      case 'Active': return outline ? 'badge text-bg-success-subtle border border-success-subtle text-success' : 'badge text-bg-success';
      case 'Released': return outline ? 'badge text-bg-success-subtle border border-success-subtle text-success' : 'badge text-bg-success';
      case 'Expired': return outline ? 'badge text-bg-danger-subtle border border-danger-subtle text-danger' : 'badge text-bg-danger';
      default: return '';
    }
  }

  getDepositAndWithdrawalStatusClass(status: string, outline: boolean = false): string {
    status = (status || '').toLowerCase().charAt(0).toUpperCase() + (status || '').slice(1);

    switch (status) {
      case 'Confirmed':
      case 'Completed':
      case 'Success':
      case 'Successful':
        return outline
          ? 'badge text-bg-success-subtle border border-success-subtle text-success'
          : 'badge text-bg-success';

      case 'Pending':
      case 'PendingSubmission':
        return outline
          ? 'badge text-bg-warning-subtle border border-warning-subtle text-warning'
          : 'badge text-bg-warning';

      case 'Processing':
      case 'Submitted':
      case 'UnderReview':
        return outline
          ? 'badge text-bg-info-subtle border border-info-subtle text-info'
          : 'badge text-bg-info';

      case 'Failed':
      case 'Rejected':
      case 'Error':
        return outline
          ? 'badge text-bg-danger-subtle border border-danger-subtle text-danger'
          : 'badge text-bg-danger';

      case 'Cancelled':
      case 'Canceled':
        return outline
          ? 'badge text-bg-secondary-subtle border border-secondary-subtle text-secondary'
          : 'badge text-bg-secondary';

      default:
        return outline
          ? 'badge text-bg-warning-subtle border border-warning-subtle text-warning'
          : 'badge text-bg-warning';
    }
  }

  getKycApplicationStatusClass(status: string, outline: boolean = false): string {
    switch (status) {
      case 'Draft': return outline ? 'badge text-bg-secondary-subtle border border-secondary-subtle text-secondary' : 'badge text-bg-secondary';
      case 'PendingSubmission': return outline ? 'badge text-bg-primary-subtle border border-primary-subtle text-primary' : 'badge text-bg-primary';
      case 'Submitted': return outline ? 'badge text-bg-info-subtle border border-info-subtle text-info' : 'badge text-bg-info';
      case 'UnderReview': return outline ? 'badge text-bg-warning-subtle border border-warning-subtle text-warning' : 'badge text-bg-warning';
      case 'Approved': return outline ? 'badge text-bg-success-subtle border border-success-subtle text-success' : 'badge text-bg-success';
      case 'Rejected': return outline ? 'badge text-bg-danger-subtle border border-danger-subtle text-danger' : 'badge text-bg-danger';
      case 'Expired': return outline ? 'badge text-bg-warning-subtle border border-warning-subtle text-warning' : 'badge text-bg-warning';
      default: return outline ? 'badge text-bg-dark-subtle border border-dark-subtle text-dark' : 'badge text-bg-dark';
    }
  }
}
